import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CircularProgress,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { formatOrderDate, formatOrderMoney } from './orderUtils';
import { getOrderStatus } from './orderStatus';

const FIELD_LABELS = {
  firstName: 'Име',
  surname: 'Фамилия',
  phone: 'Телефон',
  email: 'Имейл',
  country: 'Държава',
  streetAddress: 'Адрес',
  city: 'Град',
  county: 'Област',
  postcode: 'Пощенски код',
  paymentType: 'Плащане',
  shippingType: 'Доставка',
  shippingPrice: 'Цена за доставка',
  subtotal: 'Междинна сума',
  total: 'Общо',
  note: 'Бележка',
  status: 'Статус',
  paymentStatus: 'Статус на плащането',
  paidAt: 'Платена на'
};

const moneyFields = new Set(['shippingPrice', 'subtotal', 'total']);
const paymentStatusLabel = (value) => (value === 'paid' ? 'Платена' : 'Неплатена');

const displayValue = (field, value, currency) => {
  if (field === 'status') return getOrderStatus(value).label;
  if (field === 'paymentStatus') return paymentStatusLabel(value);
  if (field === 'paidAt') return value ? formatOrderDate(value) : '—';
  if (moneyFields.has(field)) return formatOrderMoney(value, currency);
  return value === null || value === undefined || value === '' ? '—' : String(value);
};

const itemSummary = (items = []) =>
  items.length
    ? items.map((item) => `${item.productName || 'Продукт'} × ${item.quantity}`).join(', ')
    : 'Няма продукти';

function ChangeDetails({ change }) {
  const before = change.changeData?.before || {};
  const after = change.changeData?.after || {};

  if (change.eventType === 'status_changed') {
    return (
      <Typography variant="body2">
        {displayValue('status', before.status)} → {displayValue('status', after.status)}
      </Typography>
    );
  }
  if (change.eventType === 'order_deleted') {
    return <Typography variant="body2">Поръчката е маркирана като изтрита.</Typography>;
  }
  if (change.eventType === 'payment_status_changed') {
    return (
      <Typography variant="body2">
        {paymentStatusLabel(before.paymentStatus)} → {paymentStatusLabel(after.paymentStatus)}
      </Typography>
    );
  }
  if (change.eventType === 'updated_order_email_sent') {
    return (
      <Typography variant="body2">
        Изпратен е актуализиран имейл до {change.changeData?.recipient || 'клиента'}.
      </Typography>
    );
  }

  const beforeDetails = before.details || {};
  const afterDetails = after.details || {};
  const changedFields = Object.keys({ ...beforeDetails, ...afterDetails }).filter(
    (field) => JSON.stringify(beforeDetails[field]) !== JSON.stringify(afterDetails[field])
  );
  const itemsChanged = JSON.stringify(before.items || []) !== JSON.stringify(after.items || []);

  return (
    <Stack spacing={1.5}>
      {changedFields.map((field) => (
        <Box key={field}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {FIELD_LABELS[field] || field}
          </Typography>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
            {displayValue(field, beforeDetails[field], beforeDetails.currency)} →{' '}
            {displayValue(field, afterDetails[field], afterDetails.currency)}
          </Typography>
        </Box>
      ))}
      {itemsChanged && (
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            Продукти
          </Typography>
          <Typography variant="body2">Преди: {itemSummary(before.items)}</Typography>
          <Typography variant="body2">След: {itemSummary(after.items)}</Typography>
        </Box>
      )}
    </Stack>
  );
}

export default function OrderHistory({ orderId, refreshKey = 0 }) {
  const [changes, setChanges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isCurrent = true;
    setIsLoading(true);
    setError('');
    axios
      .get(`/orders/${orderId}/history`)
      .then(({ data }) => {
        if (isCurrent) setChanges(data.changes || []);
      })
      .catch(() => {
        if (isCurrent) setError('Историята не можа да бъде заредена.');
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [orderId, refreshKey]);

  return (
    <Card sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" gap={1} sx={{ p: 3 }}>
        <HistoryRoundedIcon color="primary" />
        <Typography variant="h6">История на промените</Typography>
      </Stack>
      <Divider />
      {isLoading ? (
        <Stack alignItems="center" sx={{ p: 4 }}>
          <CircularProgress size={28} aria-label="Зареждане на историята" />
        </Stack>
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      ) : changes.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 3 }}>
          Все още няма записани промени.
        </Typography>
      ) : (
        changes.map((change) => (
          <Accordion key={change.id} disableGutters elevation={0} square>
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
              <Box>
                <Typography fontWeight={700}>
                  {change.eventType === 'status_changed'
                    ? `Статус: ${getOrderStatus(change.changeData?.after?.status).label}`
                    : change.eventType === 'order_deleted'
                      ? 'Поръчката е изтрита'
                    : change.eventType === 'payment_status_changed'
                      ? `Плащане: ${paymentStatusLabel(change.changeData?.after?.paymentStatus)}`
                    : change.eventType === 'updated_order_email_sent'
                      ? 'Изпратен е актуализиран имейл'
                    : 'Поръчката е редактирана'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {change.actorName || change.actorEmail} · {formatOrderDate(change.createdAt)} ·
                  версия {change.revision}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <ChangeDetails change={change} />
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Card>
  );
}
